import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import AnnouncementBar from "@/components/AnnouncementBar";
import BrandLogo from "@/components/BrandLogo";
import VoiceOrb from "@/components/VoiceOrb";
import {
  NeonCylinder, NeonCubes, NeonSphere, NeonKnot,
  NeonMic, NeonHeart, NeonSparkle, NeonTrend, NeonBook, NeonUsers,
} from "@/components/NeonIcons";
import { ConversaMockup, ChartMockup } from "@/components/Mockups";
import { ArrowRight, Check, Sparkles, Star } from "lucide-react";

const SHOW_PRICING = false;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.05 * i, duration: 0.65, ease: [0.16, 1, 0.3, 1] }
  })
};

export default function Landing() {
  const heroRef = useRef(null);
  const { hash } = useLocation();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const pyramidY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  // Direct loads of /#pricing etc.: the section only exists after React mounts,
  // so the browser's native hash scroll misses — do it ourselves.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="min-h-screen bg-brand-bg text-white relative overflow-hidden" data-testid="landing-page">
      {/* Background layers */}
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 grid-lines pointer-events-none opacity-60" />
      <div className="absolute inset-0 mesh-overlay pointer-events-none opacity-50" />

      <AnnouncementBar />
      <AppHeader />

      {/* ====================== HERO ====================== */}
      <section ref={heroRef} className="relative bloom-top overflow-hidden hero-vignette pb-40 pt-20 lg:pt-28">
        {/* Decorative side lines with star */}
        <div className="deco-line hidden md:block" style={{ left: "8%" }} />
        <div className="deco-line hidden md:block" style={{ right: "8%" }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center z-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="inline-flex pill-badge mb-9"
          >
            <span className="dot" />
            <span className="font-ui">ValiosIA conecta sua marca à sua cliente</span>
          </motion.div>

          <motion.h1
            style={{ y: titleY }}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display text-[48px] sm:text-7xl lg:text-[100px] leading-[0.92] tracking-[-0.035em] text-white mb-7"
          >
            Inteligência que <br className="hidden sm:inline" />
            <span className="pink-glow-text">conversa</span> com a <br className="hidden sm:inline" /> sua <em className="not-italic">marca.</em>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="text-[16px] sm:text-[17.5px] leading-relaxed text-brand-ink2 max-w-xl mx-auto mb-12"
          >
            Plataforma de conteúdo estratégico para clínicas de depilação a laser.
            Configure sua marca por conversa, defina sua cliente ideal em profundidade
            e gere Reels, Stories e copy alinhados com a sua voz — sem ferir compliance.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="flex flex-wrap items-center justify-center gap-3 mb-10"
          >
            <Link
              data-testid="hero-cta-signup"
              to="/signup"
              className="pill-dark-pink inline-flex items-center gap-3 px-8 py-3.5 text-[14px] tracking-wide"
            >
              <span>Configurar minha marca</span>
              <span className="text-brand-rose font-mono text-[12px]">it&apos;s free</span>
            </Link>
            <Link
              data-testid="hero-cta-login"
              to="/login"
              className="pill-ghost inline-flex items-center gap-2 px-7 py-3.5 text-[13.5px] font-ui"
            >
              Já tenho conta <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>

        {/* Hero pyramid glow */}
        <motion.div style={{ y: pyramidY }} className="hero-pyramid" />

        {/* Centered orb */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={4}
          className="relative z-10 flex justify-center -mt-4"
        >
          <div className="relative">
            <div className="absolute -inset-12 bg-brand-rose/20 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <VoiceOrb state="speaking" size={300} />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Logos row */}
      <section className="relative z-10 -mt-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center text-[12px] tracking-[0.22em] uppercase text-brand-ink3 font-ui mb-6">
            Construindo conteúdo estratégico para
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {["Lumière Estética", "Studio Vênus", "Clínica Aura", "Pelle Studio", "Casa Belle", "Atelier Glow"].map((b) => (
              <div key={b} className="font-display text-[18px] tracking-tight text-brand-ink2/60 hover:text-brand-ink2 transition-colors">
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== STATS / NEON ICONS ====================== */}
      <section id="features" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 z-10 section-light overflow-hidden scroll-mt-28">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-120px" }}
          className="max-w-3xl mb-16"
        >
          <div className="pill-badge mb-5"><span className="dot" /> O que a ValiosIA faz</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] tracking-tight">
            O equilíbrio perfeito entre <span className="pink-glow-text">estratégia e intimidade.</span>
          </h2>
          <p className="text-brand-ink2 max-w-lg mt-5 text-[15px] leading-relaxed">
            Reduza ciclos de ideação e elimine bloqueio de tela em branco com agentes que pensam como especialistas.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <NeonStat icon={NeonCylinder} value="100%" label="por conversa" desc="Onboarding sem formulário. A IA conduz." />
          <NeonStat icon={NeonCubes} value="12" label="campos por avatar" desc="Profundidade real — sem demografia rasa." />
          <NeonStat icon={NeonSphere} value="11" label="agentes prontos" desc="Cada um pensa como um especialista." />
          <NeonStat icon={NeonKnot} value="100%" label="compliance" desc="CFM/Anvisa embutida em cada prompt." />
        </div>
      </section>

      {/* ====================== PROCESS BENTO (Mooni big cards) ====================== */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 z-10 bloom-side-l overflow-hidden">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-120px" }}
          className="text-center mb-14 max-w-2xl mx-auto"
        >
          <div className="pill-badge mb-5 mx-auto"><span className="dot" /> Pipeline criativo</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] tracking-tight">
            <span className="pink-glow-text">Criação inteligente</span> — sem perder a sua voz.
          </h2>
        </motion.div>

        {/* Big cards */}
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          <BentoBig title="Sua marca em conversa" subtitle="Brand Persona Builder" desc="Capture bandeira, tom e posicionamento na fala — sem formulário.">
            <ConversaMockup />
          </BentoBig>
          <BentoBig title="Sua cliente em 7 blocos" subtitle="Avatar Builder" desc="Entrevista de profundidade. Dor, frustração, medo, desejo, objeção e linguagem real.">
            <ChartMockup />
          </BentoBig>
        </div>

        {/* Smaller cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <BentoSmall icon={NeonTrend} title="Performance" desc="Sequência editorial alinhada ao funil — atração, relacionamento, venda." />
          <BentoSmall icon={NeonBook} title="Educação ética" desc="Conteúdo informativo sem promessa de cura ou sensacionalismo." />
          <BentoSmall icon={NeonHeart} title="Conexão real" desc="Reels de relacionamento que conversam de gente pra gente." />
          <BentoSmall icon={NeonSparkle} title="Voz da marca" desc="Cada artefato sai impregnado da sua identidade." />
        </div>
      </section>

      {/* ====================== HOW IT WORKS ====================== */}
      <section id="how" className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-32 z-10 scroll-mt-28">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="pill-badge mb-5 mx-auto"><span className="dot" /> Como funciona</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02]">
            Três conversas. <span className="pink-glow-text">Conteúdo o ano inteiro.</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-10">
          <Step n="01" title="Sua marca em 5 minutos" text="Entrevista da Brand Persona: propósito, tom, personalidade, posicionamento, diferencial." />
          <Step n="02" title="Sua cliente em 12 campos" text="Entrevista de 7 blocos cria um avatar profundo, com a linguagem REAL da sua cliente." />
          <Step n="03" title="Acione um agente" text="Reels, Stories, Venda ou Educativo. Cada um cruza marca + avatar e entrega artefato pronto." />
        </div>
      </section>

      {/* ====================== AGENTS GRID ====================== */}
      <section id="agents" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 z-10 bloom-side-r overflow-hidden scroll-mt-28">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-5"
        >
          <div>
            <div className="pill-badge mb-5"><span className="dot" /> Os agentes</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] max-w-2xl">
              Onze especialistas. <span className="pink-glow-text">Uma só voz</span> — a sua.
            </h2>
          </div>
          <p className="text-brand-ink2 max-w-md text-[15px] leading-relaxed">
            Cada agente cruza sua Brand Persona com o avatar ativo. O resultado: roteiros que parecem escritos pela sua clínica.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AgentTile icon={NeonSparkle} title="Brand Persona Builder" desc="Tom, bandeira, propósito e posicionamento — por conversa." />
          <AgentTile icon={NeonUsers} title="Avatar Builder" desc="Entrevista de 7 blocos. Dores, frustrações, medos, desejos, objeções." />
          <AgentTile icon={NeonMic} title="Reels de Atração" desc="Roteiros para parar o scroll e ganhar seguidores qualificados." />
          <AgentTile icon={NeonHeart} title="Reels de Relacionamento" desc="Conteúdo que fortalece o vínculo com quem já segue." />
          <AgentTile icon={NeonTrend} title="Conteúdo de Venda" desc="Copy que converte com ética — sem milagre ou cura." />
          <AgentTile icon={NeonBook} title="Conteúdo Educativo" desc="Ensina, gera autoridade, elimina dúvidas técnicas." />
          <AgentTile icon={NeonMic} title="Stories Gatilho" desc="Sequências curtas que abrem conversa no direct." />
          <AgentTile icon={NeonTrend} title="Precificação" desc="Preço, combo e margem — sem conta de cabeça." />
          <AgentTile icon={NeonSparkle} title="Mentora ValiosIA" desc="Conselho de negócio na voz da metodologia." />
        </div>
      </section>

      {/* Pricing oculto até os planos estarem à venda. Código em PricingSection. */}
      {SHOW_PRICING && <PricingSection />}

      {/* ====================== TESTIMONIAL ====================== */}
      <section className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20 z-10">
        <div className="grid md:grid-cols-2 gap-5">
          <Testimonial
            name="Camila Rocha"
            handle="@camila.estetica"
            role="Dona, Studio Vênus"
            text="Era eu travada na frente do feed o dia inteiro. Agora a ValiosIA conhece minhas clientes melhor que eu. Saí de 1 post por semana pra 4 — e com mais cara minha."
          />
          <Testimonial
            name="Mariana Lopes"
            handle="@mari.lumiere"
            role="Sócia, Lumière Estética"
            text="O que mais me ajudou foi a entrevista do avatar. Eu vi coisas da minha cliente que eu nunca tinha parado pra escrever. Os Reels saem em 15 minutos."
          />
        </div>
      </section>

      {/* ====================== CTA ====================== */}
      <section className="relative py-24 lg:py-32 overflow-hidden bloom-top">
        <div className="relative max-w-3xl mx-auto px-6 text-center z-10">
          <BrandLogo size="lg" />
          <h2 className="font-display text-4xl sm:text-6xl text-white mt-10 mb-6 leading-[1.02] tracking-tight">
            Pronto para conversar com a <span className="pink-glow-text">sua marca?</span>
          </h2>
          <p className="text-brand-ink2 text-[16px] mb-11 max-w-xl mx-auto leading-relaxed">
            Comece pela Brand Persona — leva menos de 10 minutos. Sem cartão de crédito.
          </p>
          <Link
            data-testid="cta-final-signup"
            to="/signup"
            className="pill-pink inline-flex items-center gap-2 px-9 py-4 text-[14px] tracking-wide"
          >
            Configurar minha marca
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ====================== FOOTER ====================== */}
      <footer className="relative border-t border-brand-line py-14 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-[2fr_1fr_1fr_1fr] gap-10 mb-12">
            <div>
              <BrandLogo size="md" />
              <p className="text-[13px] text-brand-ink2 mt-4 max-w-xs leading-relaxed">
                Metodologia do Instituto Valiosas de Treinamentos, em forma de plataforma. Para clínicas que querem voz, não ruído.
              </p>
            </div>
            <FooterCol
              title="Produto"
              items={[
                { label: "Agentes", href: "#agents" },
                { label: "Recursos", href: "#features" },
                { label: "Como funciona", href: "#how" },
                ...(SHOW_PRICING ? [{ label: "Planos", href: "#pricing" }] : []),
              ]}
            />
            <FooterCol
              title="Recursos"
              items={[
                { label: "Compliance CFM/Anvisa", href: "#features" },
                { label: "Metodologia", to: "/sobre" },
                { label: "Os agentes", href: "#agents" },
              ]}
            />
            <FooterCol
              title="Empresa"
              items={[
                { label: "Sobre", to: "/sobre", testid: "footer-sobre" },
                { label: "Contato", href: "mailto:contato@valiosia.com.br", testid: "footer-contato" },
                { label: "Termos de Uso", to: "/termos", testid: "footer-termos" },
                { label: "Privacidade", to: "/privacidade", testid: "footer-privacidade" },
              ]}
            />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-6 border-t border-brand-line">
            <div className="text-[12px] text-brand-ink3 font-ui">
              © {new Date().getFullYear()} ValiosIA — Instituto Valiosas de Treinamentos
            </div>
            <div className="flex gap-2 text-[11px] font-ui text-brand-ink3">
              <span className="px-2.5 py-1 rounded-full bg-brand-elevated border border-brand-line">PT-BR</span>
              <span className="px-2.5 py-1 rounded-full bg-brand-elevated border border-brand-line">v1.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- subcomponents ---------- */

function NeonStat({ icon: Icon, value, label, desc }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-100px" }}
      className="card-hover-glow relative rounded-2xl border border-brand-line bg-brand-surface/60 backdrop-blur-sm p-6 overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-rose/12 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="neon-icon-frame mb-7">
          <Icon />
        </div>
        <div className="font-display text-[44px] leading-none text-white mb-2 tracking-tight">{value}</div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-rose font-ui mb-3">{label}</div>
        <p className="text-[13.5px] text-brand-ink2 leading-snug">{desc}</p>
      </div>
    </motion.div>
  );
}

function BentoBig({ title, subtitle, desc, children }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-100px" }}
      className="card-hover-glow relative rounded-3xl border border-brand-line bg-brand-surface/60 backdrop-blur-sm p-6 overflow-hidden"
    >
      <div className="mb-5">{children}</div>
      <div className="px-1">
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-rose font-ui mb-2">{subtitle}</div>
        <h3 className="font-display text-2xl text-white mb-1.5 tracking-tight">{title}</h3>
        <p className="text-[13.5px] text-brand-ink2 leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}

function BentoSmall({ icon: Icon, title, desc }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-100px" }}
      className="card-hover-glow relative rounded-2xl border border-brand-line bg-brand-surface/55 backdrop-blur-sm p-6 overflow-hidden"
    >
      <div className="neon-icon-frame mb-5" style={{ width: 48, height: 48, borderRadius: 12 }}>
        <Icon />
      </div>
      <h3 className="font-display text-lg text-white mb-1.5 tracking-tight">{title}</h3>
      <p className="text-[13px] text-brand-ink2 leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function AgentTile({ icon: Icon, title, desc }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-100px" }}
      className="card-hover-glow relative rounded-2xl border border-brand-line bg-brand-surface/55 backdrop-blur-sm p-6 overflow-hidden"
    >
      <div className="neon-icon-frame mb-5" style={{ width: 48, height: 48, borderRadius: 12 }}>
        <Icon />
      </div>
      <h3 className="font-display text-xl text-white mb-2 tracking-tight">{title}</h3>
      <p className="text-[13.5px] text-brand-ink2 leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function Step({ n, title, text }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="text-center px-2"
    >
      <div className="font-display text-7xl text-brand-rose/30 mb-3 tracking-tight">{n}</div>
      <h3 className="font-display text-2xl text-white mb-3 tracking-tight">{title}</h3>
      <p className="text-[14.5px] text-brand-ink2 leading-relaxed max-w-xs mx-auto">{text}</p>
    </motion.div>
  );
}

function Testimonial({ name, handle, role, text }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="rounded-2xl border border-brand-line bg-brand-surface/55 backdrop-blur-sm p-7"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-display text-lg"
          style={{ background: "linear-gradient(135deg, #BE9AFB, #C12862)" }}
        >
          {name[0]}
        </div>
        <div>
          <div className="text-[14px] text-white font-medium">{name}</div>
          <div className="text-[11.5px] text-brand-ink3 font-mono">{handle} · {role}</div>
        </div>
        <div className="ml-auto flex gap-0.5">
          {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-brand-rose text-brand-rose" />)}
        </div>
      </div>
      <p className="text-[14.5px] text-brand-ink2 leading-relaxed">{text}</p>
    </motion.div>
  );
}

function FooterCol({ title, items }) {
  const linkCls = "text-[13px] text-brand-ink2 hover:text-white transition-colors";
  return (
    <div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-white font-ui mb-4 font-medium">{title}</div>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.label}>
            {it.to ? (
              <Link to={it.to} data-testid={it.testid} className={linkCls}>{it.label}</Link>
            ) : it.href ? (
              <a href={it.href} data-testid={it.testid} className={linkCls}>{it.label}</a>
            ) : (
              <span className={`${linkCls} cursor-default`}>{it.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PricingSection() {
  const [yearly, setYearly] = useState(false);
  return (
    <section id="pricing" className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-32 z-10 section-light scroll-mt-28">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="text-center mb-14 max-w-2xl mx-auto"
      >
        <div className="pill-badge mb-5 mx-auto"><span className="dot" /> Planos</div>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.02] tracking-tight mb-6">
          Comece grátis. <span className="pink-glow-text">Cresça quando quiser.</span>
        </h2>
        <div className="inline-flex items-center gap-3 text-[13px] font-ui">
          <span className={!yearly ? "text-white" : "text-brand-ink3"}>Mensal</span>
          <button
            onClick={() => setYearly(!yearly)}
            className={`toggle-pill ${yearly ? "on" : ""}`}
            data-testid="pricing-toggle"
            aria-label="Alternar plano mensal/anual"
          >
            <span className="knob" />
          </button>
          <span className={yearly ? "text-white" : "text-brand-ink3"}>
            Anual <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-rose/20 text-brand-rose font-medium">−30%</span>
          </span>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5">
        <PricingCard
          name="Starter" sub="Comece a falar."
          price="Grátis"
          features={["1 Brand Persona", "1 Avatar", "Reels de Atração", "Biblioteca básica"]}
          accent={false}
        />
        <PricingCard
          name="Pro" sub="Conteúdo no ritmo certo."
          price={yearly ? "R$ 79" : "R$ 119"}
          per="/mês"
          features={["Até 5 Avatares", "Todos os agentes geradores", "Calendário editorial", "Biblioteca ilimitada", "Compliance CFM/Anvisa"]}
          accent
        />
        <PricingCard
          name="Premium" sub="Para times e estúdios."
          price={yearly ? "R$ 219" : "R$ 309"}
          per="/mês"
          features={["Avatares ilimitados", "Voice agent (Gemini Live)", "Métricas + Precificação", "Acesso multi-usuário", "Suporte prioritário"]}
          accent={false}
        />
      </div>
    </section>
  );
}

function PricingCard({ name, sub, price, per, features, accent }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className={`relative rounded-3xl p-7 backdrop-blur-sm overflow-hidden ${
        accent
          ? "border-2 border-brand-rose/50 bg-gradient-to-b from-brand-rose/10 to-brand-surface/60"
          : "border border-brand-line bg-brand-surface/55"
      }`}
    >
      {accent && (
        <>
          <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-brand-rose/30 blur-3xl pointer-events-none" />
          <span className="absolute top-5 right-5 callout-chip">Mais popular</span>
        </>
      )}
      <div className="relative">
        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-rose font-ui mb-2">{name}</div>
        <h3 className="font-display text-2xl text-white mb-1">{sub}</h3>
        <div className="flex items-baseline gap-1.5 mt-5 mb-7">
          <span className="font-display text-5xl text-white tracking-tight">{price}</span>
          {per && <span className="text-[13px] text-brand-ink2 font-ui">{per}</span>}
        </div>
        <Link
          to="/signup"
          className={`block w-full text-center py-3 rounded-full font-ui text-[13px] tracking-wide mb-6 transition-all ${
            accent ? "pill-pink" : "pill-dark-pink"
          }`}
          data-testid={`pricing-cta-${name.toLowerCase()}`}
        >
          Começar
        </Link>
        <ul className="space-y-2.5">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[13.5px] text-brand-ink2">
              <Check className="w-4 h-4 text-brand-rose mt-0.5 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}
